/**
 * ProcessingReflectionView Component
 *
 * Full-page centered view shown when a reflection is being transcribed.
 * Displays a large spinner with a single processing stage indicator.
 * Uses Server-Sent Events (SSE) for real-time updates instead of polling.
 *
 * Note: This view is only shown during initial transcription.
 * Once transcription completes, the main UI is shown with the transcript
 * and speaker confirmation happens within that context.
 */

import { Loader2, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { usePipelineEvents } from "@/hooks/usePipelineEvents";
import { useAuth } from "@/hooks/useAuth";
import type { PipelineStage } from "@/types/pipeline";

interface ProcessingReflectionViewProps {
  /** Reflection ID to monitor status for */
  reflectionId: string;
  /** Callback when processing completes (transcription done, ready for main UI) */
  onComplete?: () => void;
  /** Callback when processing fails */
  onError?: (error: string) => void;
}

interface ProcessingStageDisplay {
  stage: PipelineStage;
  title: string;
  description: string;
}

/**
 * Processing stages mapped to frontend display
 * Only transcription is shown here - speaker confirmation, feedback, and insights
 * happen after the user sees the main UI with the transcript.
 */
const PROCESSING_STAGES: ProcessingStageDisplay[] = [
  {
    stage: "transcription",
    title: "Transcribing audio",
    description: "Converting speech to text with speaker identification",
  },
];

/**
 * Determine which stage is currently active
 */
function getCurrentStageIndex(
  currentStage: PipelineStage,
  isComplete: boolean,
  isFailed: boolean
): number {
  if (isComplete) return PROCESSING_STAGES.length; // All stages complete
  if (isFailed) {
    // Return the failed stage index
    const index = PROCESSING_STAGES.findIndex((s) => s.stage === currentStage);
    return index >= 0 ? index : 0;
  }

  const index = PROCESSING_STAGES.findIndex((s) => s.stage === currentStage);
  return index >= 0 ? index : 0;
}

/**
 * Full-page processing view component
 *
 * Displays centered card with large spinner, stage indicators,
 * and helpful messaging during audio transcription.
 * Uses SSE for real-time updates.
 */
export function ProcessingReflectionView({
  reflectionId,
  onComplete,
  onError,
}: ProcessingReflectionViewProps) {
  const { getAccessToken } = useAuth();

  // Pipeline events with stage change callback
  const { status, isComplete, isFailed, reconnect } = usePipelineEvents({
    reflectionId,
    onStageChange: (stage: PipelineStage) => {
      // When speaker_confirmation stage starts, transcription is complete
      // Signal to parent that main UI can be shown
      if (stage === "speaker_confirmation") {
        onComplete?.();
      }
    },
    onComplete,
    onError: onError ? (err) => onError(err.message) : undefined,
    getAccessToken,
  });

  /**
   * Get the progress percentage for a stage
   * Returns null if progress is not meaningful (0 or 100)
   */
  const getStageProgress = (stage: PipelineStage): number | null => {
    const stageInfo = status?.stages?.[stage];
    if (!stageInfo) return null;
    // Only show percentage for in-progress stages with meaningful progress
    if (
      stageInfo.status === "in_progress" &&
      stageInfo.progress > 0 &&
      stageInfo.progress < 100
    ) {
      return stageInfo.progress;
    }
    return null;
  };

  const currentStageIndex = getCurrentStageIndex(
    status?.current_stage || "upload",
    isComplete,
    isFailed
  );

  return (
    <div className="flex items-center justify-center min-h-[600px] py-12">
      <div className="w-full max-w-2xl mx-auto px-4">
        <div className="bg-card rounded-2xl border border-border shadow-sm p-8 md:p-12">
          {/* Icon at top - changes based on state */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              {isFailed ? (
                <div className="h-24 w-24 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <AlertCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
                </div>
              ) : (
                <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center">
                  <Loader2 className="h-12 w-12 animate-spin text-foreground" />
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-center text-foreground mb-4">
            {isFailed ? "Processing Failed" : "Transcribing Your Reflection"}
          </h1>

          {/* Description */}
          {isFailed ? (
            <div className="text-center mb-8">
              <p className="text-muted-foreground mb-4">
                Something went wrong while processing your reflection.
              </p>
              {status?.error && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 max-w-md mx-auto">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    {status.error.message}
                  </p>
                </div>
              )}
              {status?.error?.recoverable && (
                <Button onClick={reconnect} variant="outline" className="gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground mb-12 leading-relaxed">
              We're converting your audio to text and identifying speakers. This
              typically takes 1-3 minutes depending on the length of your
              recording.
            </p>
          )}

          {/* Processing stages */}
          <div className={cn("space-y-6", isFailed ? "mb-8" : "mb-12")}>
            {PROCESSING_STAGES.map((stage, index) => {
              const isActive = index === currentStageIndex;
              const isCompleted = index < currentStageIndex;
              const isPending = index > currentStageIndex;
              const isStageFailed = isFailed && isActive;
              const stageProgress = getStageProgress(stage.stage);

              return (
                <div
                  key={stage.title}
                  className={cn(
                    "flex items-start gap-4 transition-all duration-300",
                    isActive && !isFailed && "scale-[1.02]"
                  )}
                >
                  {/* Stage icon */}
                  <div className="flex-shrink-0 pt-0.5">
                    <div
                      className={cn(
                        "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                        isActive &&
                          !isFailed &&
                          "bg-foreground text-background",
                        isStageFailed &&
                          "bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400",
                        isCompleted && "bg-foreground text-background",
                        isPending && "bg-muted text-muted-foreground"
                      )}
                    >
                      {isActive && !isFailed && (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      )}
                      {isStageFailed && <AlertCircle className="h-5 w-5" />}
                      {isCompleted && <CheckCircle className="h-5 w-5" />}
                      {isPending && (
                        <div className="h-2.5 w-2.5 rounded-full bg-muted" />
                      )}
                    </div>
                  </div>

                  {/* Stage text */}
                  <div className="flex-1 min-w-0">
                    <h3
                      className={cn(
                        "text-base font-semibold mb-1 transition-colors duration-300",
                        isActive && !isFailed && "text-foreground",
                        isStageFailed && "text-red-700 dark:text-red-300",
                        isCompleted && "text-foreground",
                        isPending && "text-muted-foreground"
                      )}
                    >
                      {stage.title}
                      {isActive && !isFailed && stageProgress !== null && (
                        <span className="text-muted-foreground font-normal ml-2">
                          ({stageProgress}%)
                        </span>
                      )}
                      {isCompleted && (
                        <span className="text-muted-foreground font-normal ml-2">
                          (100%)
                        </span>
                      )}
                    </h3>
                    <p
                      className={cn(
                        "text-sm transition-colors duration-300",
                        isActive && !isFailed && "text-muted-foreground",
                        isStageFailed && "text-red-600 dark:text-red-400",
                        isCompleted && "text-muted-foreground",
                        isPending && "text-muted-foreground"
                      )}
                    >
                      {isStageFailed ? "Failed" : stage.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Divider */}
          <div className="h-px bg-muted mb-6" />

          {/* Footer message */}
          <p className="text-center text-sm text-muted-foreground">
            {isFailed
              ? "Please try uploading your reflection again or contact support if the problem persists."
              : "You'll be able to view your transcript shortly. Feel free to close this page."}
          </p>
        </div>
      </div>
    </div>
  );
}
